import React, { useState } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface TaliazTransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    task?: any;
    token: string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

export const TaliazTransferModal: React.FC<TaliazTransferModalProps> = ({
    isOpen,
    onClose,
    task,
    token,
    onSuccess,
    onStatusMessage,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [moveClinicStatus, setMoveClinicStatus] = useState<string>('none');
    const [comments, setComments] = useState<string>('');

    // Reset state when modal opens and populate with task data
    React.useEffect(() => {
        if (isOpen && task) {
            setMoveClinicStatus('none');
            setComments(task.move_clinic_comments || '');
        }
    }, [isOpen, task]);

    // Handle copy payment link to clipboard
    const handleCopyPaymentLink = async () => {
        if (!task?.payment_link) return;

        try {
            await navigator.clipboard.writeText(task.payment_link);
            onStatusMessage?.({ type: 'success', text: 'Payment link copied to clipboard' });
        } catch (error) {
            onStatusMessage?.({ type: 'error', text: 'Failed to copy payment link' });
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!task || !token) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Saving Taliaz transfer...' });

        try {
            const response = await apiService.movePatient({
                token,
                patient_id: task.id,
                move_clinic_status: moveClinicStatus,
                move_clinic_comments: comments,
            });

            // Check if task should be removed
            if (response.op === 'delete') {
                onStatusMessage?.({ type: 'success', text: 'Task removed successfully' });
            } else {
                onStatusMessage?.({ type: 'success', text: 'Taliaz transfer saved successfully' });
            }

            onSuccess?.();
            handleClose();
        } catch (error: any) {
            console.error('Failed to save Taliaz transfer:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to save Taliaz transfer'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle close
    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    // Get the title from the task issue name
    const modalTitle = task?.issue || 'Taliaz Transfer';

    return (
        <ModalWrapper modalId="TaliazTransferModal"
            isOpen={isOpen}
            onClose={handleClose}
            title={modalTitle}
            isLoading={isSubmitting}
        >
            {/* Content Container - Flex Layout with Height */}
            <div className="flex flex-col min-h-80">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6">
                    <div className="space-y-4">
                        {/* Status Dropdown */}
                        <div className="flex items-center gap-4">
                            <select
                                value={moveClinicStatus}
                                onChange={(e) => setMoveClinicStatus(e.target.value)}
                                className={`${POPUP_STYLES.input} flex-1`}
                            >
                                <option value="none">None</option>
                                <option value="Interested">Interested</option>
                                <option value="Not Interested">Not Interested</option>
                            </select>
                        </div>

                        {/* Comment Field */}
                        <div className="flex items-start gap-4">
                            <label className="font-semibold text-[#366A75] min-w-[100px] pt-2">Comment</label>
                            <textarea
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                className={`${POPUP_STYLES.input} flex-1`}
                                placeholder="Enter value"
                                rows={3}
                            />
                        </div>

                        {/* Payment Link */}
                        {task?.payment_link && (
                            <div className="flex items-center gap-2">
                                <a
                                    href={task.payment_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 text-[#366A75] border border-[#366A75] rounded hover:bg-[#D0E7E5] transition-colors"
                                    style={{ textDecoration: 'underline' }}
                                >
                                    Payment link
                                </a>
                                <button
                                    onClick={handleCopyPaymentLink}
                                    className="p-2 text-[#366A75] hover:text-[#2D5A60] transition-colors"
                                    title="Copy payment link"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions - Sticky at Bottom */}
                <div className="px-6 py-2 border-t border-[#B4D9D4]">
                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={handleSave}
                            disabled={isSubmitting}
                            className={POPUP_STYLES.button}
                        >
                            Save
                        </button>
                        <button
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className={POPUP_STYLES.button}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </ModalWrapper>
    );
};
